import { useEffect, useRef, useState } from 'react';
import { FileTextOutlined } from '@ant-design/icons';
import { message, Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { Link, useBlocker } from 'react-router-dom';
// import { useBlocker } from 'react-router-dom';
import { useTypedParams } from 'react-router-typesafe-routes/dom';

import { useResource } from 'app/common/api';
import { AppPage } from 'app/common/app-page/features/app-page/app-page';
import {
  NotificationsProvider,
  useNotifyFactory,
} from 'app/common/notifications/features/notifications';
import { appRoute } from 'app/railways/router/routes';
import { PageContent } from 'app/ui';

import { CanAccess } from '../../access/features/permissions';
import { TReportTemplate } from '../core/report';
import { checkUnsavedChangesExistedFile } from '../modules/report-designer/check-unsaved-changes';
import { ReportDesignerContainer } from '../modules/report-designer/report-designer-container';
import { useReportDesignerController } from '../modules/report-designer/use-report-designer-controller';
import { getReportTemplateFileRequest } from '../requests/get-report-template-file.request';
import { getReportTemplateRequest } from '../requests/get-report-template.request';
import { updateReportTemplateRequest } from '../requests/update-report-template.request';

import styles from './details-reports.page.module.css';

type FormFields = { name: string; file?: Blob };

type UpdatedReportTemplate = FormFields & Pick<TReportTemplate, 'id'>;

export const EditReportsPage = observer(() => {
  const { id } = useTypedParams(appRoute.REPORT_EDIT);

  const pageNotify = useNotifyFactory();

  const [reportData, setReportData] = useState<TReportTemplate>({
    id: Number(id),
    name: '',
    fileName: '',
  });

  const getReportTemplateResource = useResource<
    Pick<TReportTemplate, 'id'>,
    TReportTemplate
  >({
    request: getReportTemplateRequest,
    errorNotify: pageNotify,
    onDone: (reportData) => {
      setReportData(reportData);
    },
  });

  const getReportFileResource = useResource<Pick<TReportTemplate, 'id'>, Blob>({
    request: getReportTemplateFileRequest,
    onFail(error) {
      messageRef.current?.();
      message.error({
        content: error.message,
        getPopupContainer: () => {
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.zIndex = 'calc(infinity)';
          document.body.appendChild(container);

          return container;
        },
      });
    },
    onDone: (tplFile) => {
      setTemplateFile(tplFile);
      messageRef.current?.();
    },
  });

  const messageRef = useRef<CallableFunction | null>(null);

  const [reportOpenedTime] = useState<number>(Date.now());
  const [reportSavedTime, setReportSavedTime] = useState<number>(0);

  const [templateFile, setTemplateFile] = useState<Blob | undefined>(undefined);

  const updateReportDesignerResource = useResource<
    UpdatedReportTemplate,
    TReportTemplate
  >({
    request: updateReportTemplateRequest,
    onFail(error) {
      messageRef.current?.();
      message.error({
        content: error.message,
        getPopupContainer: () => {
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.zIndex = 'calc(infinity)';
          document.body.appendChild(container);

          return container;
        },
      });
    },
    onDone: (updatedReportData) => {
      messageRef.current?.();
      setReportSavedTime(Date.now());
      setReportData(updatedReportData);

      // Перезаписываем текущий файл репорта,
      // потому что после обновления стейт сбрасывается
      // и репорт в редакторе заменяется на старый

      const editedReport = reportDesigner.controller.report?.saveToJsonString();
      if (!editedReport)
        throw new Error('Ошибка замещения отредактированного файла шаблона');

      const fileBlob = new Blob([editedReport], {
        type: 'application/json',
      });

      setTemplateFile(fileBlob);

      message.success({
        type: 'success',
        content: 'Шаблон отчета сохранен',
        getPopupContainer: () => {
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.zIndex = 'calc(infinity)';
          document.body.appendChild(container);

          return container;
        },
      });
    },
  });

  const reportDesigner = useReportDesignerController({
    onSaveReport(event) {
      event.preventDefault = true;

      messageRef.current = message.loading({
        type: 'loading',
        content: 'Сохраняем шаблон отчета на сервер...',
        getPopupContainer: () => {
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.zIndex = 'calc(infinity)';
          document.body.appendChild(container);

          return container;
        },
      });

      const jsonReport = event.report.saveToJsonString();
      const fileBlob = new Blob([jsonReport], {
        type: 'application/json',
      });

      updateReportDesignerResource.send({
        id: Number(id),
        file: fileBlob,
        name: reportData.name,
      });
    },
  });

  useEffect(() => {
    if (!templateFile) return;
    if (reportData.name === '') return;

    reportDesigner.attachReportFile({
      file: templateFile,
      name: reportDesigner.controller.report?.reportName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateFile, reportData.name]);

  useEffect(() => {
    if (!id) return;

    messageRef.current = message.loading({
      type: 'loading',
      content: 'Загрузка шаблона отчета...',
      getPopupContainer: () => {
        const container = document.createElement('div');
        container.style.position = 'relative';
        container.style.zIndex = 'calc(infinity)';
        document.body.appendChild(container);

        return container;
      },
    });

    getReportTemplateResource.send({ id });
    getReportFileResource.send({ id });
  }, [getReportFileResource, getReportTemplateResource, id]);

  const routeBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    const reportChangedTime =
      reportDesigner.controller.report?.reportChanged.jsDate.getTime() || 0;
    const reportCreatedTime = reportOpenedTime || 0;

    const hasUnsavedChanges = checkUnsavedChangesExistedFile({
      reportChangedTime,
      reportSavedTime,
      reportCreatedTime,
    });

    const isTransitionBlocked =
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname;

    return isTransitionBlocked;
  });

  useEffect(() => {
    if (routeBlocker.state === 'blocked') {
      const userResponse = confirm(
        'Последние изменения в шаблоне не были сохранены на сервер. Продолжить переход на другую страницу?',
      );

      if (userResponse) {
        routeBlocker.proceed();
      }

      return () => routeBlocker.reset();
    }
  }, [routeBlocker]);

  const reportViewPath = appRoute.REPORT.buildPath({ id: Number(id) });

  return (
    <div className={styles.root}>
      <NotificationsProvider value={pageNotify}>
        <AppPage perm="PERM_REPORT_TEMPLATE_UPDATE" style={{ height: '100%' }}>
          <Tabs
            tabBarExtraContent={
              <Link to={reportViewPath}>
                <FileTextOutlined style={{ marginRight: 8 }} />
                Открыть отчет
              </Link>
            }
            style={{ marginTop: 1, height: '100%' }}
            tabBarStyle={{
              margin: 0,
              background: 'white',
              padding: '0 16px',
            }}
          >
            <Tabs.TabPane
              tab="Редактор шаблона отчета"
              key="report-editor"
              style={{ height: '100%' }}
              forceRender
            >
              <CanAccess
                perm="PERM_REPORT_TEMPLATE_UPDATE"
                fallback="У вас нет доступа к этой функции"
              >
                <PageContent style={{ height: '100%' }}>
                  <ReportDesignerContainer
                    reportDesignerController={reportDesigner.controller}
                  />
                </PageContent>
              </CanAccess>
            </Tabs.TabPane>
          </Tabs>
        </AppPage>
      </NotificationsProvider>
    </div>
  );
});
